/**
 * Enhanced Invitation Manager Component
 * Multi-source player import: Manual, Contacts, Spreadsheet, Buddies
 * AI-powered team suggestions and player management
 */
import React, { useState, useEffect, useRef } from 'react';
import { parseOptionalHandicap, handicapLabel, hasHandicap } from '../../../utils/handicap';
import { 
  Mail, Phone, Send, UserPlus, Check, X, Clock, RefreshCw, Upload,
  Users, FileSpreadsheet, Contact, Sparkles, Search, Plus, Trash2,
  Download, AlertCircle, CheckCircle2, Loader2, Brain, Wand2
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../../components/ui/dialog';
import { Textarea } from '../../../components/ui/textarea';
import { Switch } from '../../../components/ui/switch';
import { toast } from 'sonner';
import { useOutingApi } from '../hooks/useOutingApi';
import { useOuting } from '../context/OutingContext';

// CSV Parser helper
const parseCSV = (text) => {
  const lines = text.trim().split('\n');
  const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
  const players = [];
  
  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(',').map(v => v.trim().replace(/^["']|["']$/g, ''));
    if (values.length >= 1 && values[0]) {
      const player = {};
      headers.forEach((header, idx) => {
        player[header] = values[idx] || '';
      });
      // Map common header variations
      players.push({
        name: player.name || player.player_name || player.fullname || player['full name'] || `Player ${i}`,
        email: player.email || player.mail || player['e-mail'] || '',
        phone: player.phone || player.mobile || player.cell || player.telephone || '',
        handicap: player.handicap ?? player.hdcp ?? player.hcp ?? ''
      });
    }
  }
  return players;
};

// Contacts API helper (uses Contact Picker API where available)
const pickContacts = async () => {
  if ('contacts' in navigator && 'ContactsManager' in window) {
    try {
      const props = ['name', 'email', 'tel'];
      const opts = { multiple: true };
      const contacts = await navigator.contacts.select(props, opts);
      return contacts.map(c => ({
        name: c.name?.[0] || 'Unknown',
        email: c.email?.[0] || '',
        phone: c.tel?.[0] || ''
      }));
    } catch (err) {
      console.error('Contact picker error:', err);
      return null;
    }
  }
  return null; // API not available
};

export default function InvitationManager({ outingId, user, onRefresh, teamSize = 4 }) {
  const api = useOutingApi();
  const { invitations, fetchInvitations, teams } = useOuting();
  const fileInputRef = useRef(null);
  
  // UI State
  const [showAddModal, setShowAddModal] = useState(false);
  const [importMode, setImportMode] = useState('manual'); // manual, contacts, spreadsheet, buddies, ai
  const [sending, setSending] = useState(false);
  const [bulkPlayers, setBulkPlayers] = useState([]);
  const [selectedPlayers, setSelectedPlayers] = useState(new Set());
  const [buddySearch, setBuddySearch] = useState('');
  const [buddies, setBuddies] = useState([]);
  const [loadingBuddies, setLoadingBuddies] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [contactsSupported, setContactsSupported] = useState(false);
  
  // Single player form
  const [newInvite, setNewInvite] = useState({
    player_name: '',
    email: '',
    phone: '',
    send_method: 'email'
  });

  useEffect(() => {
    fetchInvitations(outingId);
    // Check if Contacts API is available
    setContactsSupported('contacts' in navigator && 'ContactsManager' in window);
  }, [outingId, fetchInvitations]);

  // Fetch buddies for import
  const fetchBuddies = async () => {
    setLoadingBuddies(true);
    try {
      // First get current user, then fetch their buddies
      const meResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
        credentials: 'include'
      });
      const meData = await meResponse.json();
      const userId = meData.user_id || user?.user_id;
      
      if (!userId) {
        console.error('No user ID available');
        setLoadingBuddies(false);
        return;
      }
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/${userId}/buddies`, {
        credentials: 'include'
      });
      const data = await response.json();
      setBuddies(data.buddies || []);
    } catch (err) {
      console.error('Failed to fetch buddies:', err);
    } finally {
      setLoadingBuddies(false);
    }
  };

  // Handle single invite
  const handleSendInvite = async () => {
    if (!newInvite.player_name || (!newInvite.email && !newInvite.phone)) {
      toast.error('Please provide name and email or phone');
      return;
    }
    
    setSending(true);
    try {
      await api.sendInvitation(outingId, newInvite, user.user_id);
      toast.success('Invitation sent!');
      setShowAddModal(false);
      setNewInvite({ player_name: '', email: '', phone: '', send_method: 'email' });
      fetchInvitations(outingId);
      onRefresh?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSending(false);
    }
  };

  // Handle bulk invite
  const handleBulkInvite = async () => {
    const playersToInvite = bulkPlayers.filter((_, i) => selectedPlayers.has(i));
    if (playersToInvite.length === 0) {
      toast.error('Please select players to invite');
      return;
    }
    
    setSending(true);
    let successCount = 0;
    let failCount = 0;
    
    for (const player of playersToInvite) {
      try {
        await api.sendInvitation(outingId, {
          player_name: player.name,
          email: player.email,
          phone: player.phone,
          send_method: player.email ? 'email' : 'sms'
        }, user.user_id);
        successCount++;
      } catch (err) {
        failCount++;
      }
    }
    
    if (successCount > 0) {
      toast.success(`Sent ${successCount} invitation${successCount > 1 ? 's' : ''}`);
      fetchInvitations(outingId);
      onRefresh?.();
    }
    if (failCount > 0) {
      toast.error(`${failCount} invitation${failCount > 1 ? 's' : ''} failed`);
    }
    
    setShowAddModal(false);
    setBulkPlayers([]);
    setSelectedPlayers(new Set());
    setSending(false);
  };

  // Handle file upload
  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target.result;
        const players = parseCSV(text);
        if (players.length === 0) {
          toast.error('No valid players found in file');
          return;
        }
        setBulkPlayers(players);
        setSelectedPlayers(new Set(players.map((_, i) => i)));
        toast.success(`Found ${players.length} player${players.length > 1 ? 's' : ''}`);
      } catch (err) {
        toast.error('Failed to parse file. Please check format.');
      }
    };
    reader.readAsText(file);
    e.target.value = ''; // Reset input
  };

  // Handle contacts import
  const handleContactsImport = async () => {
    const contacts = await pickContacts();
    if (contacts === null) {
      toast.error('Contacts access not available. Please use spreadsheet import.');
      return;
    }
    if (contacts.length === 0) {
      toast.info('No contacts selected');
      return;
    }
    setBulkPlayers(contacts);
    setSelectedPlayers(new Set(contacts.map((_, i) => i)));
    toast.success(`Selected ${contacts.length} contact${contacts.length > 1 ? 's' : ''}`);
  };

  // Toggle player selection
  const togglePlayerSelection = (index) => {
    const newSelected = new Set(selectedPlayers);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedPlayers(newSelected);
  };

  // Select/Deselect all
  const toggleSelectAll = () => {
    if (selectedPlayers.size === bulkPlayers.length) {
      setSelectedPlayers(new Set());
    } else {
      setSelectedPlayers(new Set(bulkPlayers.map((_, i) => i)));
    }
  };

  // Add buddy to bulk list
  const addBuddyToList = (buddy) => {
    const newPlayer = {
      name: buddy.name,
      email: buddy.email || '',
      phone: buddy.phone || '',
      handicap: buddy.handicap ?? ''
    };
    setBulkPlayers([...bulkPlayers, newPlayer]);
    setSelectedPlayers(new Set([...selectedPlayers, bulkPlayers.length]));
    toast.success(`Added ${buddy.name}`);
  };

  // AI Team Suggestion
  const getAISuggestion = async () => {
    setAiLoading(true);
    try {
      // Build context for AI
      const acceptedPlayers = invitations.filter(i => i.status === 'accepted');
      const pendingPlayers = invitations.filter(i => i.status === 'pending');
      
      const prompt = `Golf Outing Assistant: Analyze this player list and suggest optimal ${teamSize}-person teams.

Accepted Players (${acceptedPlayers.length}):
${acceptedPlayers.map(p => `- ${p.player_name} (HDCP: ${handicapLabel(p.handicap)})`).join('\n') || 'None yet'}

Pending Invitations (${pendingPlayers.length}):
${pendingPlayers.map(p => `- ${p.player_name}`).join('\n') || 'None'}

Current Teams: ${teams?.length || 0}

Provide:
1. A brief status summary
2. How many more players are needed (if any)
3. Team formation suggestions if you have enough players
4. Tips for filling remaining spots`;

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/outings/ai-assistant`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ prompt, outing_id: outingId })
      });
      
      const data = await response.json();
      setAiSuggestion(data.response || data.message || 'AI assistant is processing your request...');
    } catch (err) {
      console.error('AI suggestion error:', err);
      // Fallback to local logic
      const accepted = invitations.filter(i => i.status === 'accepted').length;
      const needed = Math.ceil(accepted / teamSize) * teamSize - accepted;
      setAiSuggestion(
        `📊 **Current Status**\n` +
        `- ${accepted} players confirmed\n` +
        `- ${invitations.filter(i => i.status === 'pending').length} invitations pending\n\n` +
        `💡 **Suggestion**\n` +
        `${needed > 0 
          ? `You need ${needed} more player${needed > 1 ? 's' : ''} to complete your teams (${teamSize}-person teams).` 
          : `Great! You have enough players for ${Math.floor(accepted / teamSize)} complete team${Math.floor(accepted / teamSize) > 1 ? 's' : ''}.`
        }\n\n` +
        `🎯 **Tips**\n` +
        `- Send reminders to pending invites\n` +
        `- Consider inviting from your buddies list\n` +
        `- Upload a spreadsheet for bulk invites`
      );
    } finally {
      setAiLoading(false);
    }
  };

  // Resend invitation
  const handleResend = async (invitation) => {
    try {
      await api.sendInvitation(outingId, {
        player_name: invitation.player_name,
        email: invitation.email,
        phone: invitation.phone,
        send_method: invitation.send_method
      }, user.user_id);
      toast.success('Invitation resent!');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const pending = invitations.filter(i => i.status === 'pending');
  const accepted = invitations.filter(i => i.status === 'accepted');
  const declined = invitations.filter(i => i.status === 'declined');

  // Download template
  const downloadTemplate = () => {
    const template = 'Name,Email,Phone,Handicap\nJohn Smith,john@example.com,+15551234567,12\nJane Doe,jane@example.com,+15559876543,18';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'player_import_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      {/* Header Actions */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-white uppercase tracking-wide">Invitations</h3>
          <p className="text-sm text-zinc-400">{invitations.length} total invitations</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchInvitations(outingId)}
            className="border-white/20"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Dialog open={showAddModal} onOpenChange={(open) => {
            setShowAddModal(open);
            if (open) {
              setImportMode('manual');
              setBulkPlayers([]);
              setSelectedPlayers(new Set());
            }
          }}>
            <DialogTrigger asChild>
              <Button size="sm" className="bg-[#007AFF] hover:bg-[#0056b3]" data-testid="add-player-btn">
                <UserPlus className="w-4 h-4 mr-2" />
                Add Player
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#121212] border-white/10 text-white max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
              <DialogHeader>
                <DialogTitle className="text-xl">Add Players</DialogTitle>
              </DialogHeader>
              
              {/* Import Mode Tabs */}
              <div className="flex gap-1 p-1 bg-[#0D0D0D] rounded-lg">
                {[
                  { id: 'manual', icon: UserPlus, label: 'Manual' },
                  { id: 'spreadsheet', icon: FileSpreadsheet, label: 'Spreadsheet' },
                  { id: 'buddies', icon: Users, label: 'Buddies' },
                  { id: 'ai', icon: Sparkles, label: 'AI Help' }
                ].map(mode => (
                  <button
                    key={mode.id}
                    data-testid={`invite-mode-${mode.id}`}
                    onClick={() => {
                      setImportMode(mode.id);
                      if (mode.id === 'buddies' && buddies.length === 0) fetchBuddies();
                      if (mode.id === 'ai' && !aiSuggestion) getAISuggestion();
                    }}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-2 rounded-md text-xs font-medium transition-all ${
                      importMode === mode.id 
                        ? 'bg-[#007AFF] text-white' 
                        : 'text-zinc-400 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <mode.icon className="w-3.5 h-3.5" />
                    {mode.label}
                  </button>
                ))}
              </div>
              
              {/* Content Area */}
              <div className="flex-1 overflow-y-auto py-4 min-h-0">
                {/* Manual Entry */}
                {importMode === 'manual' && (
                  <div className="space-y-4">
                    <div>
                      <Label>Player Name *</Label>
                      <Input
                        value={newInvite.player_name}
                        onChange={(e) => setNewInvite({ ...newInvite, player_name: e.target.value })}
                        placeholder="John Smith"
                        className="bg-[#1A1A1A] border-white/20 mt-1"
                        data-testid="invite-name-input"
                      />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={newInvite.email}
                        onChange={(e) => setNewInvite({ ...newInvite, email: e.target.value })}
                        placeholder="john@example.com"
                        className="bg-[#1A1A1A] border-white/20 mt-1"
                      />
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <Input
                        type="tel"
                        value={newInvite.phone}
                        onChange={(e) => setNewInvite({ ...newInvite, phone: e.target.value })}
                        placeholder="+1 555 123 4567"
                        className="bg-[#1A1A1A] border-white/20 mt-1"
                      />
                    </div>
                    <div>
                      <Label>Send Via</Label>
                      <div className="flex gap-2 mt-2">
                        {['email', 'sms', 'both'].map(method => (
                          <Button
                            key={method}
                            size="sm"
                            variant={newInvite.send_method === method ? 'default' : 'outline'}
                            onClick={() => setNewInvite({ ...newInvite, send_method: method })}
                            className={newInvite.send_method === method ? 'bg-[#007AFF]' : 'border-white/20'}
                          >
                            {method === 'email' && <Mail className="w-3 h-3 mr-1" />}
                            {method === 'sms' && <Phone className="w-3 h-3 mr-1" />}
                            {method.charAt(0).toUpperCase() + method.slice(1)}
                          </Button>
                        ))}
                      </div>
                    </div>
                    <Button
                      onClick={handleSendInvite}
                      disabled={sending}
                      className="w-full bg-[#007AFF] hover:bg-[#0056b3]"
                      data-testid="send-invitation-btn"
                    >
                      {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                      Send Invitation
                    </Button>
                  </div>
                )}

                {/* Spreadsheet Import */}
                {importMode === 'spreadsheet' && (
                  <div className="space-y-4">
                    <div className="border-2 border-dashed border-white/20 rounded-xl p-6 text-center">
                      <FileSpreadsheet className="w-10 h-10 text-zinc-500 mx-auto mb-3" />
                      <p className="text-zinc-400 text-sm mb-3">
                        Upload a CSV file with player information
                      </p>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv,.txt"
                        onChange={handleFileUpload}
                        className="hidden"
                      />
                      <div className="flex gap-2 justify-center">
                        <Button
                          onClick={() => fileInputRef.current?.click()}
                          className="bg-[#007AFF] hover:bg-[#0056b3]"
                          data-testid="choose-file-btn"
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          Choose File
                        </Button>
                        <Button
                          variant="outline"
                          onClick={downloadTemplate}
                          className="border-white/20"
                          data-testid="download-template-btn"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Template
                        </Button>
                      </div>
                    </div>
                    
                    {/* Contacts Button (if supported) */}
                    {contactsSupported && (
                      <div className="text-center">
                        <p className="text-zinc-500 text-xs mb-2">or</p>
                        <Button
                          variant="outline"
                          onClick={handleContactsImport}
                          className="border-white/20"
                        >
                          <Contact className="w-4 h-4 mr-2" />
                          Import from Contacts
                        </Button>
                      </div>
                    )}
                    
                    {/* Preview List */}
                    {bulkPlayers.length > 0 && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label>{bulkPlayers.length} Players Found</Label>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={toggleSelectAll}
                            className="text-xs text-[#007AFF]"
                          >
                            {selectedPlayers.size === bulkPlayers.length ? 'Deselect All' : 'Select All'}
                          </Button>
                        </div>
                        <div className="max-h-48 overflow-y-auto space-y-1 border border-white/10 rounded-lg p-2">
                          {bulkPlayers.map((player, idx) => (
                            <div
                              key={idx}
                              onClick={() => togglePlayerSelection(idx)}
                              className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all ${
                                selectedPlayers.has(idx) 
                                  ? 'bg-[#007AFF]/20 border border-[#007AFF]/50' 
                                  : 'hover:bg-white/5 border border-transparent'
                              }`}
                            >
                              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                selectedPlayers.has(idx) ? 'border-[#007AFF] bg-[#007AFF]' : 'border-white/30'
                              }`}>
                                {selectedPlayers.has(idx) && <Check className="w-3 h-3 text-white" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-white font-medium text-sm truncate">{player.name}</p>
                                <p className="text-zinc-500 text-xs truncate">
                                  {player.email || player.phone || 'No contact info'}
                                </p>
                              </div>
                              {(
                                <Badge variant="outline" className="text-xs">
                                  HDCP {handicapLabel(player.handicap)}
                                </Badge>
                              )}
                            </div>
                          ))}
                        </div>
                        <Button
                          onClick={handleBulkInvite}
                          disabled={sending || selectedPlayers.size === 0}
                          className="w-full bg-green-600 hover:bg-green-700"
                        >
                          {sending ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Send className="w-4 h-4 mr-2" />
                          )}
                          Invite {selectedPlayers.size} Player{selectedPlayers.size !== 1 ? 's' : ''}
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* Buddies Import */}
                {importMode === 'buddies' && (
                  <div className="space-y-4">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <Input
                        value={buddySearch}
                        onChange={(e) => setBuddySearch(e.target.value)}
                        placeholder="Search your golf buddies..."
                        className="bg-[#1A1A1A] border-white/20 pl-10"
                      />
                    </div>
                    
                    {loadingBuddies ? (
                      <div className="text-center py-8">
                        <Loader2 className="w-6 h-6 text-[#007AFF] animate-spin mx-auto" />
                        <p className="text-zinc-500 text-sm mt-2">Loading buddies...</p>
                      </div>
                    ) : buddies.length === 0 ? (
                      <div className="text-center py-8">
                        <Users className="w-10 h-10 text-zinc-600 mx-auto mb-2" />
                        <p className="text-zinc-400">No buddies found</p>
                        <p className="text-zinc-500 text-sm">Add golf buddies from your profile</p>
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {buddies
                          .filter(b => 
                            !buddySearch || 
                            b.name?.toLowerCase().includes(buddySearch.toLowerCase()) ||
                            b.email?.toLowerCase().includes(buddySearch.toLowerCase())
                          )
                          .map(buddy => {
                            const alreadyInvited = invitations.some(
                              i => i.email === buddy.email || i.player_name === buddy.name
                            );
                            return (
                              <div
                                key={buddy.user_id || buddy.email}
                                className="flex items-center justify-between p-3 bg-[#1A1A1A] rounded-lg"
                              >
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 bg-[#007AFF]/20 rounded-full flex items-center justify-center">
                                    <span className="text-[#007AFF] font-bold">
                                      {buddy.name?.charAt(0)?.toUpperCase() || '?'}
                                    </span>
                                  </div>
                                  <div>
                                    <p className="text-white font-medium">{buddy.name}</p>
                                    <p className="text-zinc-500 text-xs">{buddy.email || buddy.phone}</p>
                                  </div>
                                </div>
                                {alreadyInvited ? (
                                  <Badge className="bg-green-500/20 text-green-400">Invited</Badge>
                                ) : (
                                  <Button
                                    size="sm"
                                    onClick={() => addBuddyToList(buddy)}
                                    className="bg-[#007AFF] hover:bg-[#0056b3]"
                                  >
                                    <Plus className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            );
                          })}
                      </div>
                    )}
                    
                    {/* Quick add from bulk list */}
                    {bulkPlayers.length > 0 && (
                      <div className="border-t border-white/10 pt-4">
                        <Label className="mb-2 block">{bulkPlayers.length} player(s) ready to invite</Label>
                        <Button
                          onClick={handleBulkInvite}
                          disabled={sending}
                          className="w-full bg-green-600 hover:bg-green-700"
                        >
                          <Send className="w-4 h-4 mr-2" />
                          Send All Invitations
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* AI Assistant */}
                {importMode === 'ai' && (
                  <div className="space-y-4">
                    <Card className="bg-gradient-to-br from-purple-500/10 to-blue-500/10 border-purple-500/30">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center">
                            <Brain className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <h4 className="text-white font-semibold">AI Outing Assistant</h4>
                            <p className="text-zinc-400 text-xs">Smart player & team management</p>
                          </div>
                        </div>
                        
                        {aiLoading ? (
                          <div className="flex items-center justify-center py-6">
                            <Loader2 className="w-6 h-6 text-purple-400 animate-spin" />
                            <span className="text-zinc-400 ml-2">Analyzing your outing...</span>
                          </div>
                        ) : aiSuggestion ? (
                          <div className="bg-black/30 rounded-lg p-4 text-sm" data-testid="ai-suggestion-text">
                            <pre className="text-zinc-300 whitespace-pre-wrap font-sans">
                              {aiSuggestion}
                            </pre>
                          </div>
                        ) : (
                          <div className="text-center py-4">
                            <Wand2 className="w-8 h-8 text-zinc-500 mx-auto mb-2" />
                            <p className="text-zinc-400 text-sm">Click refresh to get AI suggestions</p>
                          </div>
                        )}
                        
                        <Button
                          onClick={getAISuggestion}
                          disabled={aiLoading}
                          data-testid="ai-refresh-btn"
                          variant="outline"
                          className="w-full mt-3 border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                        >
                          <RefreshCw className={`w-4 h-4 mr-2 ${aiLoading ? 'animate-spin' : ''}`} />
                          {aiLoading ? 'Analyzing...' : 'Refresh Analysis'}
                        </Button>
                      </CardContent>
                    </Card>
                    
                    {/* Quick Actions from AI */}
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        variant="outline"
                        onClick={() => setImportMode('spreadsheet')}
                        className="border-white/20 h-auto py-3"
                      >
                        <div className="text-center">
                          <FileSpreadsheet className="w-5 h-5 mx-auto mb-1 text-[#007AFF]" />
                          <span className="text-xs">Bulk Import</span>
                        </div>
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setImportMode('buddies')}
                        className="border-white/20 h-auto py-3"
                      >
                        <div className="text-center">
                          <Users className="w-5 h-5 mx-auto mb-1 text-green-400" />
                          <span className="text-xs">From Buddies</span>
                        </div>
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Invitation Lists */}
      <Tabs defaultValue="pending">
        <TabsList className="bg-[#1A1A1A] border border-white/10">
          <TabsTrigger value="pending" className="data-[state=active]:bg-[#007AFF]">
            Pending ({pending.length})
          </TabsTrigger>
          <TabsTrigger value="accepted" className="data-[state=active]:bg-green-600">
            Accepted ({accepted.length})
          </TabsTrigger>
          <TabsTrigger value="declined" className="data-[state=active]:bg-red-600">
            Declined ({declined.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="mt-4">
          <InvitationList 
            invitations={pending} 
            status="pending"
            onResend={handleResend}
          />
        </TabsContent>

        <TabsContent value="accepted" className="mt-4">
          <InvitationList 
            invitations={accepted} 
            status="accepted"
          />
        </TabsContent>

        <TabsContent value="declined" className="mt-4">
          <InvitationList 
            invitations={declined} 
            status="declined"
            onResend={handleResend}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Invitation List Sub-component
function InvitationList({ invitations, status, onResend }) {
  if (invitations.length === 0) {
    const messages = {
      pending: 'No pending invitations',
      accepted: 'No accepted invitations yet',
      declined: 'No declined invitations'
    };
    return (
      <Card className="bg-[#1A1A1A] border-white/10">
        <CardContent className="p-6 text-center">
          <p className="text-zinc-500">{messages[status]}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-2">
      {invitations.map(inv => (
        <Card key={inv.id || inv.invitation_id || inv.email || inv.player_name} className="bg-[#1A1A1A] border-white/10">
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  status === 'accepted' ? 'bg-green-500/20' :
                  status === 'declined' ? 'bg-red-500/20' : 'bg-yellow-500/20'
                }`}>
                  {status === 'accepted' && <Check className="w-5 h-5 text-green-400" />}
                  {status === 'declined' && <X className="w-5 h-5 text-red-400" />}
                  {status === 'pending' && <Clock className="w-5 h-5 text-yellow-400" />}
                </div>
                <div>
                  <p className="text-white font-medium">{inv.player_name}</p>
                  <p className="text-zinc-500 text-xs">
                    {inv.email || inv.phone}
                    {inv.send_method && ` • via ${inv.send_method}`}
                  </p>
                </div>
              </div>
              {onResend && (status === 'pending' || status === 'declined') && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onResend(inv)}
                  className="text-[#007AFF] hover:bg-[#007AFF]/10"
                >
                  <RefreshCw className="w-4 h-4 mr-1" />
                  Resend
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
